import { lazy, type ComponentType } from 'react'

type Loader<T extends ComponentType<any>> = () => Promise<{ default: T }>
type ModuleMap = Record<string, { default: ComponentType<any> }>

const ssrModules: ModuleMap | null = import.meta.env.SSR
  ? (import.meta.glob('../pages/**/*.tsx', { eager: true }) as ModuleMap)
  : null

const normalizeSSRKeys = (ssrKey: string): string[] => {
  if (!ssrKey) return []

  const ensureExt = (value: string) => (value.endsWith('.tsx') ? value : `${value}.tsx`)
  const sanitize = (value: string) => value.replace(/\\/g, '/')

  const trimmed = ssrKey
    .replace(/^\.\//, '')
    .replace(/^src\//, '')
    .replace(/^pages\//, '')

  const candidates = new Set<string>()

  const push = (value: string) => {
    const sanitized = sanitize(value)
    candidates.add(sanitized)
    candidates.add(ensureExt(sanitized))
  }

  push(ssrKey)
  push(trimmed)
  push(`../${trimmed}`)
  push(`../pages/${trimmed}`)

  return Array.from(candidates)
}

export function loadPage<T extends ComponentType<any>>(loader: Loader<T>, ssrKey: string): ComponentType<any> {
  if (import.meta.env.SSR && ssrModules) {
    for (const key of normalizeSSRKeys(ssrKey)) {
      const module = ssrModules[key]
      if (module) {
        return module.default
      }
    }

    const available = Object.keys(ssrModules).join(', ')
    throw new Error(`SSR page module not found for "${ssrKey}". Available: [${available}]`)
  }

  return lazy(loader) as ComponentType<any>
}
